declare global {
  interface Window {
    __ENV__?: Record<string, string>;
  }
}

/**
 * Get an environment variable at runtime.
 * In production, reads from window.__ENV__ (injected by nginx at container startup).
 * In development, falls back to Vite's import.meta.env with VITE_ prefix.
 */
/**
 * Get an environment variable at runtime from `window.__ENV__`.
 *
 * - Production: nginx injects `window.__ENV__` into `env.js` at container startup (from pod env).
 * - Dev: each app's `main.tsx` must call `bootstrapDevEnv()` before rendering, which copies
 *   Vite's `import.meta.env.VITE_*` (stripped prefix) into `window.__ENV__`. This is necessary
 *   because Vite only inlines `import.meta.env` for files inside the app root — not for files
 *   in workspace packages like this one.
 */
export function getEnv(key: string): string {
  if (typeof window === "undefined") return "";
  return window.__ENV__?.[key] ?? "";
}

/**
 * Copy `import.meta.env.VITE_*` into `window.__ENV__` for dev-time access. Call from the app's
 * `main.tsx` before the router renders. Values set via nginx's runtime `env.js` take precedence.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function bootstrapDevEnv(metaEnv: Record<string, any> | undefined): void {
  if (typeof window === "undefined" || !metaEnv) return;
  const existing = window.__ENV__ ?? {};
  for (const [key, value] of Object.entries(metaEnv)) {
    if (!key.startsWith("VITE_")) continue;
    const stripped = key.substring(5);
    // Don't overwrite nginx-injected values (prod takes precedence)
    if (existing[stripped]) continue;
    existing[stripped] = String(value);
  }
  window.__ENV__ = existing;
}
